import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn, createTestIdHelper } from '../../../lib/utils';

const testId = createTestIdHelper('pager');

export const pagerTestIds = {
  root: testId('root'),
  label: testId('label'),
  previous: testId('previous'),
  next: testId('next'),
} as const;

const PAGER_BUTTON =
  'flex size-6 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition hover:bg-muted disabled:pointer-events-none disabled:opacity-40';

export interface PagerProps {
  /** Zero-based position of the item on show. */
  index: number;
  total: number;
  onChange: (index: number) => void;
  /** Already-localized position text, e.g. "2 of 3"; announced on change. */
  label: string;
  previousLabel: string;
  nextLabel: string;
  className?: string;
}

/**
 * A one-item-at-a-time pager: position text plus previous/next arrows.
 * Renders nothing for a single item; the caller owns the index and all copy.
 */
export function Pager({
  index,
  total,
  onChange,
  label,
  previousLabel,
  nextLabel,
  className,
}: PagerProps) {
  if (total <= 1) return null;

  return (
    <div
      className={cn('flex shrink-0 items-center gap-1.5', className)}
      data-testid={pagerTestIds.root}
    >
      <span
        aria-live="polite"
        className="text-xs font-medium tabular-nums text-muted-foreground"
        data-testid={pagerTestIds.label}
      >
        {label}
      </span>
      <button
        type="button"
        aria-label={previousLabel}
        disabled={index <= 0}
        onClick={() => onChange(index - 1)}
        className={PAGER_BUTTON}
        data-testid={pagerTestIds.previous}
      >
        <ChevronLeft className="size-4" aria-hidden />
      </button>
      <button
        type="button"
        aria-label={nextLabel}
        disabled={index >= total - 1}
        onClick={() => onChange(index + 1)}
        className={PAGER_BUTTON}
        data-testid={pagerTestIds.next}
      >
        <ChevronRight className="size-4" aria-hidden />
      </button>
    </div>
  );
}
